import type { Type } from '@angular/core';
import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { BehaviorSubject, of } from 'rxjs';
import type { Widgets } from '@common/enums';
import type { ExtendedWidget, WidgetParams } from '@common/types/content.type';

@Injectable({ providedIn: 'root' })
export abstract class BaseWidgetsService<W extends ExtendedWidget = ExtendedWidget> {
    protected visibleWidgets$!: BehaviorSubject<W[]>;

    protected abstract widgets: W[];
    protected readonly widgetsMap: Map<Widgets, W> = new Map<Widgets, W>();

    constructor() {
        this.init();
    }

    protected abstract init(): void;

    /**
     * Get all default widgets from configs
     */
    public getWidgets(): Observable<W[]> {
        return of(this.widgets);
    }

    /**
     * Get all visible widgets
     */
    public getVisibleWidgets$(): Observable<W[]> {
        if (!this.visibleWidgets$) {
            this.visibleWidgets$ = new BehaviorSubject(this.filterHiddenWidgets());
        }

        return this.visibleWidgets$;
    }

    /**
     * Get widget by widget name
     */
    public getWidgetModel(widgetName: Widgets = null): W | null {
        return this.widgetsMap.get(widgetName) || null;
    }

    /**
     * Get widget view component by widget name
     */
    public getWidgetViewComponent<T>(widgetName: Widgets = null): Type<T> | null {
        return this.getWidgetModel(widgetName)?.componentView || null;
    }

    /**
     * Get widget control component by widget name
     */
    public getWidgetControlComponent<T>(widgetName: Widgets = null): Type<T> | null {
        return this.getWidgetModel(widgetName)?.componentControl || null;
    }

    /**
     * Get extended widget params by widget name
     */
    public getExtendedWidgetParams(widgetName: Widgets = null): WidgetParams | null {
        return this.getWidgetModel(widgetName)?.params || null;
    }

    /**
     * Get widget default data by widget name
     */
    public getWidgetDefaultData(widgetName: Widgets = null): any | null {
        return this.getWidgetModel(widgetName)?.data || null;
    }

    /**
     * Set widget as visible
     */
    public setWidgetAsVisible(widgetName: Widgets = null): void {
        const widget = this.getWidgetModel(widgetName);

        if (!widget) {
            return;
        }

        if (widget.params) {
            widget.params.hidden = false;
        }
        this.visibleWidgets$.next(this.filterHiddenWidgets());
    }

    /**
     * Set widget as available
     */
    public setWidgetAsAvailable(widgetName: Widgets = null): void {
        const widget = this.getWidgetModel(widgetName);

        if (!widget) {
            return;
        }

        if (widget.params) {
            widget.params.disabled = false;
        }

        const widgets = this.filterHiddenWidgets().filter((w) => w.params?.type.includes('default'));

        if (!this.visibleWidgets$) {
            this.visibleWidgets$ = new BehaviorSubject(widgets);
            return;
        }

        this.visibleWidgets$.next(widgets);
    }

    public setWidgetAsDisabled(widgetName: Widgets = null): void {
        const widget = this.getWidgetModel(widgetName);

        if (!widget) {
            return;
        }
        if (widget.params) {
            widget.params.disabled = true;
        }
        this.visibleWidgets$.next(this.filterHiddenWidgets());
    }

    public setWidgetAsEnabled(widgetName: Widgets = null): void {
        const widget = this.getWidgetModel(widgetName);

        if (!widget) {
            return;
        }
        if (widget.params) {
            widget.params.disabled = false;
        }
        this.visibleWidgets$.next(this.filterHiddenWidgets());
    }

    /**
     * Filter hidden widgets
     */
    protected filterHiddenWidgets(): W[] {
        return this.widgets.filter((widget: W) => !widget?.params?.hidden);
    }
}
